import { Link, Tag } from "@caprikorn/dynamo-ui";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { NavLink, generatePath } from "react-router-dom";

import { Elec, Gas } from "@icons";
import { ROUTES } from "@router/router.constants";
import type { Site } from "@services/apiEcms/apiEcms.types";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./PerimeterSitesTable.module.css";

const columnHelper = createColumnHelper<Site>();

const getPodStatusColor = (statusCode: number) => {
  if ([40, 41, 42, 53, 81, 99].includes(statusCode)) return "error";
  if ([54, 56, 72, 73, 74, 75].includes(statusCode)) return "warning";
  if ([50, 51, 52, 55, 70, 71, 80, 90].includes(statusCode)) return "info";
  if ([91, 92, 93].includes(statusCode)) return "disabled";
  if (statusCode === 60) return "success";

  return "default";
};

export const usePerimeterSitesTableColumns = () => {
  const { t } = useCustomTranslation(["main", "common"]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("siteInformation", {
        header: t("perimeter.sites.columns.name"),
        enableSorting: false,
        meta: { isHighlighted: true },
        cell: ({ getValue }) => {
          const siteInformation = getValue();
          return (
            <Link
              as={NavLink}
              hasNoDecoration
              to={generatePath(ROUTES.PERIMETER_SITE, {
                customerId: siteInformation.customerId,
                contractId: siteInformation.contractId,
                siteId: siteInformation.siteId,
                energy:
                  siteInformation.energy === "GAZ"
                    ? "GAS"
                    : siteInformation.energy,
              })}
            >
              {siteInformation.podName}
            </Link>
          );
        },
      }),
      columnHelper.accessor("siteInformation.energy", {
        header: t("perimeter.sites.columns.type"),
        enableSorting: false,
        meta: {
          align: "center",
          innerCellClassName: classes.cell,
        },
        cell: ({ getValue }) =>
          getValue() === "ELEC" ? (
            <Tag
              color="elec"
              icon={Elec}
              iconStyle="start"
              label={t("energy.ELEC", { ns: "common" })}
              size="sm"
            />
          ) : (
            <Tag
              color="gas"
              icon={Gas}
              iconStyle="start"
              label={t("energy.GAS", { ns: "common" })}
              size="sm"
            />
          ),
      }),
      columnHelper.accessor("siteInformation.siteId", {
        header: t("perimeter.sites.columns.podReference"),
        enableSorting: false,
      }),
      columnHelper.accessor("siteInformation.status", {
        header: t("perimeter.sites.columns.podStatus"),
        enableSorting: false,
        meta: {
          align: "center",
          innerCellClassName: classes.cell,
        },
        cell: ({ getValue }) => {
          const { name, code } = getValue();

          return <Tag color={getPodStatusColor(code)} label={name} size="sm" />;
        },
      }),
      columnHelper.accessor("consumptionAddress", {
        header: t("perimeter.sites.columns.consumptionAddress"),
        enableSorting: false,
        cell: ({ getValue }) =>
          (
            [
              "streetNumber",
              "street",
              "additionalAddress",
              "zipCode",
              "city",
            ] as const
          )
            .map((key) => getValue()[key])
            .filter(Boolean)
            .join(" "),
      }),
    ],
    [t],
  );

  return columns;
};
